import { appStoreConstant } from "@/utils/branch"
import clsx from "clsx"
import { useTheme } from "next-themes"
import { Arrow } from "pages/tools"
import { CSSProperties, useState } from "react"
import { z } from "zod"
import { BeforeAfterWithSmallCircle } from "../before-after"
import Bubble from "../bubble"
import Button from "../button"

const featureAiToolSchema = z.enum([
	"deform",
	"ai_photo",
	"ai_restyle",
	"text_to_image",
	"text_to_video",
	"ai_music_generator",
])

const stepSchema = z.object({
	title: z.string(),
	description: z.string(),
})

export const tutorialSchema = z.object({
	component: z.literal("how_to_make"),
	title: z.string(),
	subtitle: z.string().optional(),
	button_text: z.string(),
	button_link: z.string().optional(),
	steps: z.array(stepSchema),
	media: z.string().optional(),
	before: z.string().optional(),
	after: z.string().optional(),
	is_video: z.boolean().optional(),
	image_alt: z.string().optional(),
	style: z.literal("large").optional(),
	title_attr: z
		.array(
			z.object({
				start: z.number(),
				length: z.number(),
				color: z.string().optional(),
				dark_color: z.string().optional(),
			}),
		)
		.optional(),
	upload: z.boolean().optional(),
	tool: featureAiToolSchema.optional(),
})

type TutorialProps = z.infer<typeof tutorialSchema>

export default function HowToMake(props: TutorialProps) {
	const [stepNumber, setStepNumber] = useState(0)
	const {
		title,
		button_text,
		button_link,
		steps,
		subtitle,
		media,
		before,
		after,
		is_video,
		image_alt,
		style,
	} = props

	function calculateHeight(
		text: string,
		index: number,
		lineLength: number,
		lineHeight: number,
	): string {
		if (stepNumber === index) {
			let height = text
				.split("\n")
				.filter((temp) => temp.length !== 0)
				.reduce(
					(prev, cur) =>
						prev +
						Math.ceil(cur.length / lineLength) * lineHeight,
					0,
				)
			height =
				height +
				((text.split("\n\n") || []).length - 1) * lineHeight
			return `${height}px`
		} else {
			return "0px"
		}
	}

	if (style && style === "large") {
		return <HowToMakeLarge {...props} />
	}

	return (
		<>
			<div className="flex w-full justify-center py-[16px] tablet:py-[40px] desktop:py-[55px]">
				<div className="atablet:overflow-hidden flex w-full flex-col items-center gap-1 tablet:w-[727px] desktop:w-[975px] desktop:px-0">
					<div className="flex flex-col items-center gap-4 text-center desktop:gap-[22px]">
						<h2 className="text-h2 tablet:text-h3 whitespace-pre-line px-4 text-center">
							{title}
						</h2>
						{subtitle && (
							<h3 className="text-body-3 px-4 text-center text-blue-700 tablet:px-16">
								{subtitle}
							</h3>
						)}
					</div>
					<div className="flex w-full flex-col items-center gap-9 tablet:flex-row">
						{!is_video && media && (
							<img
								src={media}
								alt={image_alt ?? title}
								className="pointer-events-none w-full shrink-0 rounded-[20px] object-contain tablet:h-[329px] tablet:w-[231px] desktop:h-[437px] desktop:w-[355px] desktop:rounded-2xl"
							/>
						)}
						{is_video && media && (
							<video
								autoPlay
								playsInline
								loop
								muted
								src={media}
								className="pointer-events-none w-[300px] shrink-0 rounded-[8px] object-cover tablet:h-[329px] tablet:w-[231px] desktop:h-[437px] desktop:w-[355px] desktop:rounded-[14px]"
							/>
						)}
						{before && after && (
							<div className="ah-[352px] atablet:h-[329px] adesktop:h-[437px] relative w-[281px] shrink-0 overflow-hidden rounded-[20px] tablet:w-[231px] desktop:w-[355px] desktop:rounded-2xl">
								<BeforeAfterWithSmallCircle
									before={before}
									after={after}
									video={is_video}
									className="w-full"
									labels
								/>
							</div>
						)}
						<div className="flex w-full flex-col items-center justify-between gap-3 tablet:h-full tablet:items-start">
							<div className="flex w-full flex-col gap-3">
								<div className="relative">
									<div className="pointer-events-none absolute left-0 top-0 hidden h-[50px] w-full bg-gradient-to-b from-color-cell to-[#F9F9FE00] tablet:block"></div>
									<div className="no-scrollbar flex w-full flex-row gap-3 overflow-x-scroll px-4 tablet:h-[257px] tablet:flex-col tablet:overflow-y-scroll tablet:px-0 tablet:py-11 desktop:h-[365px]">
										{steps.map((step, index) => (
											<div className="shrink-0" key={index}>
												<div className="hidden flex-col gap-2 tablet:flex">
													<button
														onClick={() => {
															setStepNumber(index)
														}}
														className="flex flex-row items-center gap-4">
														<div className="flex flex-row items-center">
															<Bubble
																className="flex !h-[26px] !w-[26px] items-center justify-center !p-0"
																text={String(index + 1)}
																color={
																	stepNumber === index ||
																	step.description.trim()
																		.length === 0
																		? "purplePinkSelected"
																		: "purplePink"
																}
															/>
														</div>
														<div className="flex flex-row items-center gap-[6px]">
															<p
																className={clsx(
																	stepNumber === index ||
																		step.description.trim()
																			.length === 0
																		? "text-body-2"
																		: "text-body-3 text-blue-600",
																)}>
																{step.title}
															</p>
															<div
																className={clsx(
																	"transition-all",
																	stepNumber === index
																		? "-rotate-90 [--icon-color:var(--color-blue-800)]"
																		: "rotate-90 [--icon-color:var(--color-blue-600)]",
																	step.description.trim()
																		.length === 0 &&
																		"hidden",
																)}>
																<Arrow />
															</div>
														</div>
													</button>
													<p
														style={
															{
																whiteSpace: "pre-line",
																"--desktop-size":
																	calculateHeight(
																		step.description,
																		index,
																		65,
																		27,
																	),
																"--tablet-size":
																	calculateHeight(
																		step.description,
																		index,
																		55,
																		24,
																	),
															} as CSSProperties
														}
														className="text-body-4 hidden overflow-hidden pl-[42px] transition-all duration-500 tablet:block tablet:max-h-[var(--tablet-size)] desktop:max-h-[var(--desktop-size)]">
														{step.description}
													</p>
												</div>
												<button
													onClick={() =>
														setStepNumber(index)
													}
													className="flex shrink-0 tablet:hidden"
													key={index}>
													<Bubble
														text={"Step " + (index + 1)}
														color={
															stepNumber === index
																? "purplePinkSelected"
																: "purplePinkWithHover"
														}
													/>
												</button>
											</div>
										))}
									</div>
									<div className="pointer-events-none absolute bottom-0 left-0 hidden h-[46px] w-full bg-gradient-to-t from-color-cell to-[#F9F9FE00] tablet:block"></div>
								</div>
								<p className="text-body-2 mt-4 block px-4 tablet:mt-3 tablet:hidden">
									{steps[stepNumber].title}
								</p>
								<p
									style={{ whiteSpace: "pre-line" }}
									className="text-body-4 block px-4 tablet:hidden">
									{steps[stepNumber].description}
								</p>
							</div>
							{button_text && (
								<>
									<Button
										href={button_link}
										className="mt-8 hidden tablet:mt-0 desktop:block"
										text={button_text}
										primary
										full
									/>
									<Button
										href={appStoreConstant}
										text={button_text}
										primary
										full
										className="mt-2 w-full tablet:mt-0 desktop:hidden"
									/>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

function HowToMakeLarge(props: TutorialProps) {
	const [stepNumber, setStepNumber] = useState(0)
	const {
		title,
		button_text,
		button_link,
		steps,
		subtitle,
		media,
		before,
		after,
		is_video,
		image_alt,
		title_attr,
	} = props

	const { resolvedTheme } = useTheme()

	function calculateHeight(
		text: string,
		index: number,
		lineLength: number,
		lineHeight: number,
	): string {
		if (stepNumber === index) {
			let height = text
				.split("\n")
				.filter((temp) => temp.length !== 0)
				.reduce(
					(prev, cur) =>
						prev +
						Math.ceil(cur.length / lineLength) * lineHeight,
					0,
				)
			height =
				height +
				((text.split("\n\n") || []).length - 1) * lineHeight
			return `${height}px`
		} else {
			return "0px"
		}
	}

	let formattedTitle: string | (string | JSX.Element)[] =
		title

	if (title_attr) {
		formattedTitle = []
		let index = 0

		for (let i = 0; i < title.length; i++) {
			const isColored = title_attr.some(
				({ start, length }) => {
					return i >= start && i < start + length
				},
			)

			const attr = title_attr.find(({ start, length }) => {
				return i >= start && i < start + length
			})

			let color = "var(--color-primary-500)"

			if (
				attr &&
				resolvedTheme === "dark" &&
				attr.dark_color
			) {
				color = attr.dark_color
			} else if (attr && attr.color) {
				color = attr.color
			}

			if (isColored) {
				formattedTitle.push(
					<span
						key={index}
						style={{ color }}
						className="text-[30px] font-700 desktop:text-[54px]">
						{title[i]}
					</span>,
				)
			} else {
				formattedTitle.push(title[i])
			}

			index++
		}
	}

	return (
		<section className="flex w-full items-center justify-center px-4 py-[35px] tablet:px-0 tablet:py-[40px] desktop:px-[calc(50vw-702px)] desktop:py-[100px]">
			<div className="flex w-full flex-col items-center gap-4 tablet:w-[727px] desktop:w-full desktop:gap-9 desktop:px-[30px]">
				<div className="flex flex-col items-center gap-4 text-center desktop:gap-[22px]">
					<h2 className="whitespace-pre-line px-4 text-center text-[30px] font-700 desktop:text-[54px]">
						{formattedTitle}
					</h2>
					{subtitle && (
						<h3 className="text-body-3 px-4 text-center text-blue-700 tablet:px-16">
							{subtitle}
						</h3>
					)}
				</div>
				<div className="flex w-full flex-col items-center gap-9 tablet:flex-row desktop:justify-between">
					{!is_video && media && (
						<img
							src={media}
							alt={image_alt ?? title}
							className="pointer-events-none max-h-[352px] w-[281px] shrink-0 rounded-[12px] object-contain tablet:max-h-[600px] tablet:w-[355px] desktop:max-h-[700px] desktop:w-[660px]"
						/>
					)}
					{is_video && media && (
						<video
							autoPlay
							playsInline
							loop
							muted
							src={media}
							className="pointer-events-none max-h-[352px] w-[281px] shrink-0 rounded-[12px] object-cover tablet:max-h-[329px] tablet:w-[231px] desktop:max-h-[700px] desktop:w-[660px]"
						/>
					)}
					{before && after && (
						<div className="relative w-[281px] shrink-0 overflow-hidden rounded-[12px] tablet:w-[231px] desktop:w-[660px]">
							<BeforeAfterWithSmallCircle
								before={before}
								after={after}
								video={is_video}
								className="w-full"
								labels
							/>
						</div>
					)}
					<div className="flex w-full flex-col items-center justify-between gap-3 tablet:h-full tablet:w-auto tablet:max-w-[355px] tablet:items-start desktop:w-auto desktop:max-w-[542px]">
						<div className="flex w-full flex-col gap-3">
							<div className="relative">
								<div className="pointer-events-none absolute left-0 top-0 hidden h-[50px] w-full bg-gradient-to-b from-color-cell to-[#F9F9FE00] tablet:block"></div>
								<div className="no-scrollbar flex w-full flex-row gap-3 overflow-x-scroll tablet:h-[257px] tablet:flex-col tablet:overflow-y-scroll tablet:px-0 tablet:py-11 desktop:h-[365px]">
									{steps.map((step, index) => (
										<div className="shrink-0" key={index}>
											<div className="hidden flex-col gap-2 tablet:flex">
												<button
													onClick={() => {
														setStepNumber(index)
													}}
													className="flex flex-row items-center gap-4">
													<div className="flex flex-row items-center">
														<Bubble
															className="flex !h-[26px] !w-[26px] items-center justify-center !p-0"
															text={String(index + 1)}
															color={
																stepNumber === index ||
																step.description.trim()
																	.length === 0
																	? "purplePinkSelected"
																	: "purplePink"
															}
														/>
													</div>
													<div className="flex flex-row items-center gap-[6px]">
														<p
															className={clsx(
																stepNumber === index ||
																	step.description.trim()
																		.length === 0
																	? "text-[18px] font-500 text-blue-900 tablet:text-[20px] desktop:text-[26px] desktop:font-600"
																	: "text-[18px] font-500 text-blue-600 desktop:text-[26px] desktop:font-600",
															)}>
															{step.title}
														</p>
														<div
															className={clsx(
																"transition-all",
																stepNumber === index
																	? "-rotate-90 [--icon-color:var(--color-blue-800)]"
																	: "rotate-90 [--icon-color:var(--color-blue-600)]",
																step.description.trim()
																	.length === 0 && "hidden",
															)}>
															<Arrow />
														</div>
													</div>
												</button>
												<p
													style={
														{
															whiteSpace: "pre-line",
															"--desktop-size":
																calculateHeight(
																	step.description,
																	index,
																	65,
																	27,
																),
															"--tablet-size":
																calculateHeight(
																	step.description,
																	index,
																	55,
																	24,
																),
														} as CSSProperties
													}
													className="text-body-4 hidden overflow-hidden pl-[42px] text-blue-700 transition-all duration-500 tablet:block tablet:max-h-[var(--tablet-size)] desktop:max-h-[var(--desktop-size)]">
													{step.description}
												</p>
											</div>
											<button
												onClick={() => setStepNumber(index)}
												className="flex shrink-0 tablet:hidden"
												key={index}>
												<Bubble
													text={"Step " + (index + 1)}
													color={
														stepNumber === index
															? "purplePinkSelected"
															: "purplePinkWithHover"
													}
												/>
											</button>
										</div>
									))}
								</div>
								<div className="pointer-events-none absolute bottom-0 left-0 hidden h-[46px] w-full bg-gradient-to-t from-color-cell to-[#F9F9FE00] tablet:block"></div>
							</div>
							<p className="text-body-2 mt-4 block tablet:mt-3 tablet:hidden">
								{steps[stepNumber].title}
							</p>
							<p
								style={{ whiteSpace: "pre-line" }}
								className="text-body-4 block tablet:hidden">
								{steps[stepNumber].description}
							</p>
						</div>
						{button_text && button_link && (
							<Button
								text={button_text}
								href={button_link}
								primary
								full
								className="mt-8"
							/>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
